import LibraryTile, { type LibraryTileThumbs, type ZoomTarget } from './LibraryTile.tsx';
import { groupCountLine, libraryGroups, type LibraryGroup } from './libraryGroups.ts';
import { isLockedLibraryCategory } from '../../../shared/library.ts';
import { cssVars } from '../../lib/css.ts';
import { Download, Plus } from '../../lib/icons.tsx';
import type { Dispatch, SetStateAction } from 'react';
import type { CategoryTreeNode } from '../../lib/categoryTree.ts';
import type { LibraryItemActions } from './LibraryItemMenu.tsx';
import type { LibraryItem, Person } from '../../../shared/api.ts';

/**
 * The Library's body: either one heading per category with its cards under it
 * (Group = Category, the default), or the flat grid (Group = None).
 *
 * The grouped shape is the design's, and it is the right default for this page
 * for the reason the decks grid is not grouped: a deck is looked for by name,
 * an item is browsed for by the kind of thing it is. Which headings exist and
 * what goes under each is `libraryGroups.ts`'s decision — this file is the
 * rendering, so the two halves that are easy to get wrong stay testable without
 * a DOM.
 *
 * Three things the grouped arm does that the flat one does not.
 *
 * **An empty category keeps its heading and gets a call to action.** A category
 * someone made and has not filled is exactly what the registry's empty paths
 * are for, and a heading with nothing under it and no way forward is the
 * unhelpful half of showing it. "Save a slide here" opens the same explainer
 * the page's own "Save from a deck" button does, naming the category, because
 * saving genuinely happens from a deck's slide menu and this page cannot
 * honestly offer to do it.
 *
 * **The built-in Case studies shelf carries the import.** Everything on it came
 * off rkh.co.uk, so that shelf, and only that shelf, ends with an "Import a
 * case study" tile — the way the Bank's People folder owns "Sync team". Exact
 * match on the path, not `categoryMatches`: a sub-shelf ("Case studies/Acme")
 * is not where an import lands, so it must not offer one. On that shelf the
 * import tile is the call to action when it is empty, too.
 *
 * **The "+ New item" ghost tile is rendered ONCE, after the last heading**,
 * rather than at the end of every group. Repeating it would put six identical
 * create buttons on one screen and make each of them look like it creates
 * something different. In the flat grid it stays where it has always been, as
 * the last cell.
 */

type Props = {
  /** Each item carries its own slides: an item owns a deck. */
  items: LibraryItem[];
  /** The people registry, so a card can turn its `ownerId` into a name. */
  people: readonly Person[];
  /** The rail's tree: the registry's display order, empty categories included. */
  tree: readonly CategoryTreeNode[];
  /** Group = Category groups; Group = None renders the flat grid. */
  grouped: boolean;
  /** True while any axis narrows the body — empty headings are dropped then. */
  filtering: boolean;
  /** Item slugs a server-side build is still writing into. */
  building: readonly string[];
  /** Item slug → what its failed build said, while the job registry remembers. */
  failures: ReadonlyMap<string, string>;
  busy: boolean;
  openMenuKey: string | null;
  setMenuKey: Dispatch<SetStateAction<string | null>>;
  actions: LibraryItemActions;
  onZoom: (target: ZoomTarget) => void;
  thumbs: LibraryTileThumbs;
  tileMin: string;
  onNewItem: () => void;
  /** The per-group call to action, given the category path it was pressed in. */
  onSaveHere: (category: string | null) => void;
  /** Opens the "Import a case study" modal — offered on the built-in shelf only. */
  onImportCaseStudy: () => void;
};

export default function LibraryBody(props: Props) {
  const { items, grouped, tree, filtering, tileMin, onNewItem } = props;
  const style = cssVars({ '--tile-min': tileMin });

  if (!grouped) {
    return (
      <ul className="library__grid" role="list" style={style}>
        {items.map((item) => (
          <Tile key={item.slug} item={item} {...props} />
        ))}
        <NewItemTile busy={props.busy} onNewItem={onNewItem} />
      </ul>
    );
  }

  const groups = libraryGroups({ items, tree, filtering });
  return (
    <div className="library__groups">
      {groups.map((group) => (
        <Group key={group.path ?? ' uncategorised'} group={group} {...props} />
      ))}
      <ul className="library__grid" role="list" style={style}>
        <NewItemTile busy={props.busy} onNewItem={onNewItem} />
      </ul>
    </div>
  );
}

function Group({ group, ...props }: Props & { group: LibraryGroup }) {
  const style = cssVars({ '--tile-min': props.tileMin });
  const headingId = `library-group-${(group.path ?? 'uncategorised').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
  const importsHere = isLockedLibraryCategory(group.path);
  return (
    <section className="library-group" data-group={group.path ?? ''} aria-labelledby={headingId}>
      <div className="library-group__head">
        {/* The registry tint when the category has one, and a neutral dot
            otherwise. A SWATCH NAME turned into a custom property, never a
            literal — the same rule the rail's dot and the filter chips follow.
            The dot is drawn either way: a heading whose dot came and went would
            leave the labels on two different left edges. */}
        <span
          className="library-group__dot"
          style={group.colour ? { background: `var(--swatch-${group.colour})` } : undefined}
          aria-hidden="true"
        />
        <h2 className="library-group__title" id={headingId}>
          {group.label}
        </h2>
        <span className="library-group__count">{groupCountLine(group.items.length)}</span>
      </div>
      {group.items.length === 0 && !importsHere ? (
        <p className="library-group__empty">
          <span className="muted small">Nothing filed here yet.</span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            // The row's own words are "Save a slide here", which says nothing
            // out of context: six of these on one page are six identical
            // buttons to a screen reader.
            aria-label={`Save a slide into ${group.label}`}
            onClick={() => props.onSaveHere(group.path)}
          >
            Save a slide here
          </button>
        </p>
      ) : (
        <ul className="library__grid" role="list" style={style}>
          {group.items.map((item) => (
            <Tile key={item.slug} item={item} {...props} />
          ))}
          {importsHere && <ImportCaseStudyTile busy={props.busy} onImport={props.onImportCaseStudy} />}
        </ul>
      )}
    </section>
  );
}

/** One card. The slides are the item's own; the owner's name is looked up here
 *  so the tile never sees the registry. */
function Tile({
  item,
  people,
  building,
  failures,
  busy,
  openMenuKey,
  setMenuKey,
  actions,
  onZoom,
  thumbs,
}: Props & { item: LibraryItem }) {
  const isBuilding = building.includes(item.slug);
  return (
    <LibraryTile
      item={item}
      owner={item.ownerId ? (people.find((p) => p.id === item.ownerId)?.name ?? null) : null}
      building={isBuilding}
      // A build that FAILED, while the registry still remembers it; never
      // both at once, since a running build is not a failed one.
      failure={isBuilding ? null : (failures.get(item.slug) ?? null)}
      busy={busy}
      menuOpen={openMenuKey === `item:${item.slug}`}
      setMenuKey={setMenuKey}
      actions={actions}
      onZoom={onZoom}
      thumbs={thumbs}
    />
  );
}

function NewItemTile({ busy, onNewItem }: { busy: boolean; onNewItem: () => void }) {
  return (
    <li className="library-tile library-tile--ghost">
      <button
        type="button"
        className="library-tile__frame library-tile__frame--empty library-tile__frame--ghost"
        onClick={onNewItem}
        disabled={busy}
        aria-label="Create a new library item"
      >
        <span className="library-tile__empty">
          <Plus /> New item
        </span>
      </button>
    </li>
  );
}

function ImportCaseStudyTile({ busy, onImport }: { busy: boolean; onImport: () => void }) {
  return (
    <li className="library-tile library-tile--ghost">
      <button
        type="button"
        className="library-tile__frame library-tile__frame--empty library-tile__frame--ghost"
        onClick={onImport}
        disabled={busy}
        aria-label="Import a case study from rkh.co.uk"
        title="Brought back from rkh.co.uk, ready to insert"
      >
        <span className="library-tile__empty">
          <Download /> Import a case study
        </span>
      </button>
    </li>
  );
}
